import { DetectionResult } from './api';

export interface SearchHistoryItem extends DetectionResult {
  id: string;
  searchedAt: string;
}

class SearchHistoryService {
  private readonly STORAGE_KEY = 'tabby-search-history';
  private readonly MAX_HISTORY_ITEMS = 50;

  getHistory(): SearchHistoryItem[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];
      
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Failed to load search history:', error);
      return [];
    }
  }

  addToHistory(result: DetectionResult): SearchHistoryItem {
    const historyItem: SearchHistoryItem = {
      ...result,
      id: this.generateId(),
      searchedAt: new Date().toISOString()
    };

    try {
      const history = this.getHistory();
      
      // Remove any existing entry for the same URL
      const filteredHistory = history.filter(item => item.url !== result.url);
      
      // Add new item at the beginning
      const newHistory = [historyItem, ...filteredHistory];
      
      // Limit to max items
      const limitedHistory = newHistory.slice(0, this.MAX_HISTORY_ITEMS);
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(limitedHistory));
      
      console.log(`✅ Added search to history: ${result.url}`);
      return historyItem;
    } catch (error) {
      console.warn('Failed to save to search history:', error);
      return historyItem;
    }
  }

  removeFromHistory(id: string): void {
    try {
      const history = this.getHistory();
      const filteredHistory = history.filter(item => item.id !== id);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredHistory));
      console.log(`✅ Removed search from history: ${id}`);
    } catch (error) {
      console.warn('Failed to remove from search history:', error);
    }
  }

  clearHistory(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      console.log('✅ Cleared search history');
    } catch (error) {
      console.warn('Failed to clear search history:', error);
    }
  }

  getById(id: string): SearchHistoryItem | null {
    const history = this.getHistory();
    return history.find(item => item.id === id) || null;
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

export const searchHistoryService = new SearchHistoryService();
